// =============================================================================
// topicProposer — LLM proponuje listę "ludzkich" podstron z tematyki serwisu.
// Zapis jako drafty (status='draft_topic') do unique_subpages.
// =============================================================================

import { supabase } from "@/integrations/supabase/client";
import { loadPrompt } from "@/services/llm/promptLoader";
import { getLLMProvider, parseLLMJSON } from "@/services/llm";
import { compilePromptSafe } from "@/services/contentEngine/promptCompiler";
import { logPublic, logError } from "@/services/contentEngine/contentLogger";
import { fetchMapSubpageContext } from "./subpageContext";
import type { SubpageTopicProposal } from "@/types/uniquePages";

export interface ProposeResult {
  status: "ok" | "skipped" | "error";
  reason?: string;
  count?: number;
}

const TOPICS_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["pages"],
  properties: {
    pages: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["kind", "title", "topic"],
        properties: {
          kind: { type: "string" },
          title: { type: "string" },
          topic: { type: "string" },
        },
      },
    },
  },
};

export async function proposeTopics(mapId: string): Promise<ProposeResult> {
  try {
    const ctx = await fetchMapSubpageContext(mapId);
    if (!ctx) return { status: "error", reason: "Nie znaleziono mapy" };
    if (!ctx.macroStatement.trim()) return { status: "skipped", reason: "Brak macro_statement (uzupełnij Macro serwisu)" };

    const prompt = await loadPrompt("unique_subpage_topics" as any);
    if (!prompt) return { status: "error", reason: "Brak promptu unique_subpage_topics" };

    const compiled = compilePromptSafe(
      prompt.template,
      { service_name: ctx.serviceName, macro_statement: ctx.macroStatement, language: ctx.language },
      "unique_subpage_topics",
      "",
    );
    if (!compiled) return { status: "error", reason: "Kompilacja promptu nie powiodła się" };

    let pages: SubpageTopicProposal[] = [];
    try {
      const provider = getLLMProvider((prompt.provider || "openai") as "openai" | "claude");
      const resp = await provider.call(compiled, {
        model: prompt.modelId || "gpt-4.1-mini",
        temperature: prompt.temperature ?? 0.5,
        jsonSchema: TOPICS_SCHEMA,
        step: "unique_subpage_topics",
        mapId,
      });
      const parsed = parseLLMJSON<{ pages: SubpageTopicProposal[] }>(resp.text);
      pages = Array.isArray(parsed?.pages) ? parsed.pages.filter((p) => p && p.title) : [];
    } catch (e) {
      logError("UNIQUE-TOPICS", `LLM padł dla map=${mapId.slice(0, 8)}`, e);
      return { status: "error", reason: e instanceof Error ? e.message : "LLM error" };
    }

    if (!pages.length) return { status: "skipped", reason: "LLM nie zwrócił propozycji" };

    const userId = (await supabase.auth.getUser()).data.user?.id ?? null;
    const rows = pages.map((p) => ({
      user_id: userId,
      map_id: mapId,
      kind: p.kind || null,
      title: p.title,
      topic: p.topic || null,
      status: "draft_topic",
    }));
    const { error } = await (supabase as any).from("unique_subpages").insert(rows);
    if (error) return { status: "error", reason: error.message };

    logPublic("UNIQUE-TOPICS", `Zaproponowano ${rows.length} podstron dla ${ctx.serviceName}`);
    return { status: "ok", count: rows.length };
  } catch (e) {
    logError("UNIQUE-TOPICS", `proposeTopics error map=${mapId.slice(0, 8)}`, e);
    return { status: "error", reason: e instanceof Error ? e.message : "wyjątek" };
  }
}
